import {
    ApolloCache,
    ApolloClient,
    ApolloClientOptions,
    HttpLink,
    InMemoryCache,
    NormalizedCacheObject,
} from '@apollo/client';
import { IncomingMessage } from 'http';
import { getCookieByName, isSsr } from '@99designs/browser';
import { default as introspectionResult } from './generated/introspectionResult.generated';
import { typePolicies } from './generated/typePolicies.generated';

export const DEFAULT_GRAPH_ENDPOINT = '/app/graphql';

const getCsrfToken = (cookie: string) => getCookieByName('_99csrf', cookie);

/**
 * Create Apollo client for SSR
 *
 * @param {IncomingMessage} req Request
 * @param {string} endpoint URI (default: DEFAULT_GRAPH_ENDPOINT)
 * @param {Partial<ApolloClientOptions<NormalizedCacheObject>>} options Optional config for Apollo client
 * @returns {ApolloClient<NormalizedCacheObject>}
 */
export function createSSRApolloClient(
    req: IncomingMessage,
    endpoint: string = DEFAULT_GRAPH_ENDPOINT,
    options?: Partial<ApolloClientOptions<NormalizedCacheObject>>
): ApolloClient<NormalizedCacheObject> {
    const getHeader = (name: string): string => (req.headers[name] || '') as string;

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const host = process.env.GRAPHQL_HOST
        ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          process.env.GRAPHQL_HOST
        : getHeader('x-forwarded-host');

    if (host.length <= 0) {
        throw new Error(
            'X-Forwarded-Host header is required but not present in request. This normally means the request has not been proxied through bastion'
        );
    }

    const headers: Record<string, string> = {
        cookie: getHeader('cookie'),
        experiments: getHeader('experiments'),
        '99-request-summary': getHeader('99-request-summary'),
        '99-request-summary-sig': getHeader('99-request-summary-sig'),
        'x-csrf-token': getCsrfToken(getHeader('cookie')),
    };

    return createApolloClient(`https://${host}${endpoint}`, headers, options);
}

/**
 * Create Apollo client for browser
 *
 * @param {string} endpoint URI (default: DEFAULT_GRAPH_ENDPOINT)
 * @param {Partial<ApolloClientOptions<NormalizedCacheObject>>} options Optional config for Apollo client
 * @returns {ApolloClient<NormalizedCacheObject>}
 */
export function createBrowserApolloClient(
    endpoint: string = DEFAULT_GRAPH_ENDPOINT,
    options?: Partial<ApolloClientOptions<NormalizedCacheObject>>
): ApolloClient<NormalizedCacheObject> {
    const cookie = typeof document !== 'undefined' ? document.cookie : '';
    const headers = {
        cookie,
        'x-csrf-token': getCsrfToken(cookie),
    };

    return createApolloClient(endpoint, headers, options);
}

/**
 * Create custom Apollo client
 *
 * @param {string} endpoint URI (default: DEFAULT_GRAPH_ENDPOINT)
 * @param {Record<string, string>} headers Optional list of headers (default: {})
 * @param {Partial<ApolloClientOptions<NormalizedCacheObject>>} options Optional config for Apollo client (default: {})
 * @returns {ApolloClient<NormalizedCacheObject>}
 */
export function createApolloClient(
    endpoint: string = DEFAULT_GRAPH_ENDPOINT,
    headers: Record<string, string> = {},
    options: Partial<ApolloClientOptions<NormalizedCacheObject>> = {}
): ApolloClient<NormalizedCacheObject> {
    const browserCookie = typeof document !== 'undefined' ? document.cookie : '';

    const link = new HttpLink({
        uri: endpoint,
        credentials: 'same-origin',
        headers: {
            ...(options?.headers || {}),
            '99-request-summary': headers?.['99-request-summary'],
            '99-request-summary-sig': headers?.['99-request-summary-sig'],
            'x-csrf-token':
                headers?.['x-csrf-token'] ||
                getCsrfToken(headers?.['cookie'] || '') ||
                getCsrfToken(browserCookie),
            cookie: headers?.['cookie'] || browserCookie,
            experiments: headers?.['experiments'],
        },
    });

    const cache: ApolloCache<NormalizedCacheObject> = new InMemoryCache({
        addTypename: true,
        typePolicies: typePolicies,
        possibleTypes: introspectionResult.possibleTypes,
    });

    return new ApolloClient({
        link,
        cache,
        defaultOptions: {
            query: {
                fetchPolicy: 'cache-first',
                errorPolicy: 'all',
            },
        },
        ssrMode: isSsr(),
        ...options,
    });
}
